import uniq from 'lodash/uniq';
import { State } from '../types';
import { CartProductSchema, OperationType } from '../../types';

/**
 *
 * While placing the order for new products, we are checking the existing product's next billing anchor time of the same cloudsite for the new product.
 * If we get a unique next billing anchor time for all products under the same product, then that will be the next billing anchor time for the new products.
 * If not, then we send the current date timestamp.
 * More details https://hello.atlassian.net/wiki/spaces/~70121d05ec16fa6f7444db10a305ee7e0df3c/pages/4569403969/Co-term+new+products+starting+on+existing+site
 */
export const getExistingNextBillDateAnchorTimeFromCloudSite = ({
  initialStoreProducts,
  cloudSite,
  nextBillDate,
}: {
  initialStoreProducts?: State['products'];
  cloudSite?: string;
  nextBillDate?: State['nextBillDate'];
}) => {
  if (!initialStoreProducts || !cloudSite) return;

  const existingProductsUnderCloudSite = Object.values(initialStoreProducts)?.filter(
    (item: CartProductSchema) => item.cloudSite === cloudSite && item.operationType !== OperationType.NEW
  );

  const selectedNextBillDateForCloudSiteProducts = existingProductsUnderCloudSite
    ?.map((product) => {
      // if user has entered any custom next bill date
      if (nextBillDate && Object.values(nextBillDate).length) {
        // setting same timestamp for each cloud site URL for all products and app (existing and new ones)
        if (product && nextBillDate.cloudSiteLevel?.[product.cloudSite]) {
          // these are timestamps and not date strings;
          return Number(nextBillDate.cloudSiteLevel?.[product.cloudSite]);
        }

        if (product && nextBillDate.entitlementLevel?.[product.id]) {
          return Number(nextBillDate.entitlementLevel?.[product.id]);
        }
      }
    })
    ?.filter(Boolean);

  const uniqueSelectedNextBillDateForCloudSiteProducts = uniq(selectedNextBillDateForCloudSiteProducts);
  // if we have only one product under the same cloud site, then we can use that as the next bill anchor date for the new products
  if (uniqueSelectedNextBillDateForCloudSiteProducts?.length === 1) {
    return uniqueSelectedNextBillDateForCloudSiteProducts[0];
  }
  const allNextBillAnchorDateForSameCloudSite = existingProductsUnderCloudSite?.map(
    (item: CartProductSchema) => item.nextBillAnchorDate
  );

  // if all the products have the same next bill anchor date, then we can use that as the next bill anchor date for the new products
  const uniqueNextBillingAnchorDateForSameCloudSite = uniq(allNextBillAnchorDateForSameCloudSite);
  if (uniqueNextBillingAnchorDateForSameCloudSite.length === 1) {
    return Number(uniqueNextBillingAnchorDateForSameCloudSite[0]);
  }
};
